import { rateCardAllowedFor, roleRef } from '@timelog/pm-shared';
import type { ContractDocument, ContractType, TimeLogRateOption } from '@timelog/pm-shared';

import type { FinancialsT } from '../metricReading';

/**
 * A time-based contract's rate card starts as a COPY of the caller's default
 * hourly rates — the same roles, at the default amounts, in the entity the
 * user works in (D3/D15/D18, decided 2026-09-15) — and the PM then edits it to
 * the agreed prices. Two moments create it: committing the hours step of the
 * T&M guided entry, and switching an existing contract's billing method to
 * T&M/retainer. Only ever onto an EMPTY card: the PM's edits and removals are
 * theirs, a re-run of guided entry does not undo them.
 *
 * Pure, so the copy and the "only when empty" rule are unit-tested; the copy
 * comes from the translation `t` passed in.
 */
export const seedRateCardFromDefaults = (
  document: ContractDocument,
  defaults: readonly TimeLogRateOption[],
  capturedAt: string = new Date().toISOString(),
  makeId: () => string = () => crypto.randomUUID(),
): ContractDocument => {
  if (document.rateCard.length > 0 || defaults.length === 0) return document;
  return {
    ...document,
    rateCard: defaults.map((rate) => ({
      id: makeId(),
      roleRef: roleRef(rate.serviceId, rate.name, capturedAt, rate.costRate),
      rate: rate.rate,
      // The role's cost price comes along (ADR 0042) — the card entry is what
      // a role-only T&M line costs at (CALC-21), and the PM may edit it later.
      ...(rate.costRate === undefined ? {} : { costRate: rate.costRate }),
    })),
  };
};

/**
 * Whether the contract carries anything a billing-method change would
 * disturb — an amount, typed hours or a rate card. A fresh contract switches
 * silently; one with budgets asks first (`ChangeBillingMethodDialog`).
 */
export const hasBudgets = (document: ContractDocument): boolean =>
  document.revenue > 0 ||
  document.rateCard.length > 0 ||
  document.categories.some(
    (category) => category.amount > 0 || category.ownHoursOverride !== undefined,
  );

export type BillingMethodChange = {
  from: ContractType;
  to: ContractType;
  /** Roles on the card today — what a switch to fixed price deletes. */
  cardSize: number;
  /** Roles the default list would copy — what a switch to T&M creates. */
  defaultRoleCount: number;
};

/** What the change does to the rate card, in the user's words; null when nothing. */
export const rateCardConsequence = (change: BillingMethodChange, t: FinancialsT): string | null => {
  const fromCard = rateCardAllowedFor(change.from);
  const toCard = rateCardAllowedFor(change.to);
  if (fromCard && !toCard && change.cardSize > 0) {
    return t('pmFinancials:billingMethodDialog.CardDeleted', { count: change.cardSize });
  }
  if (!fromCard && toCard) {
    return change.defaultRoleCount > 0
      ? t('pmFinancials:billingMethodDialog.CardCreated', { count: change.defaultRoleCount })
      : t('pmFinancials:billingMethodDialog.CardEmpty');
  }
  return null;
};
